import type { SortedTableRow } from '@hellodarwin/app-core/lib/components/common/sort-table';
import TaskTable, {
  buildTaskTableColumns,
  TaskTableCellText,
  TaskTableCenteredCell,
  TaskTableFilterBar,
  TaskTableMutedText,
  TaskTableOwnerPills,
  TaskTableSegmentedFilter,
  TaskTableStatusPill,
  TaskTableTitleButton,
  TaskTableTitleText,
  type TaskTableStatusPillTone,
} from '@hellodarwin/app-core/lib/components/common/task-table';
import Button from '@hellodarwin/core/lib/components/common/button';
import Typography from '@hellodarwin/core/lib/components/common/typography';
import Loading from '@hellodarwin/core/lib/components/loading';
import { formatUtcDate } from '@hellodarwin/core/lib/features/helpers/get-formatted-date';
import { antdMessage } from '@hellodarwin/core/lib/features/providers/antd-app-provider';
import useLocale from '@hellodarwin/core/lib/features/providers/locale-provider';
import { useTranslations } from '@hellodarwin/core/lib/features/providers/translations-provider';
import Checkbox from 'antd/es/checkbox';
import Pagination from 'antd/es/pagination/Pagination';
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import type {
  ClientTask,
  ClientTaskCounts,
  ClientTaskStatusFilter,
} from '../../features/api/client-task-types';
import { TaskError, TaskPagination, TaskSection } from './styles';
import { getTaskDaysRemaining } from './task-date';
import { TaskDetailDrawer } from './task-detail-drawer';

interface ProgramTasksProps {
  tasks: readonly ClientTask[];
  status: 'idle' | 'pending' | 'succeeded' | 'failed';
  mutationStatusByTaskId: Record<string, 'idle' | 'pending' | 'failed'>;
  applicationCompanyName?: string;
  counts?: ClientTaskCounts;
  filter?: ClientTaskStatusFilter;
  focusedTaskId?: string;
  page?: number;
  pageSize?: number;
  showApplicationColumn?: boolean;
  total?: number;
  onFilterChange?: (filter: ClientTaskStatusFilter) => void;
  onOpenApplication?: (programId: string) => void;
  onPageChange?: (page: number) => void;
  onRetry: () => void;
  onSetCompleted: (taskId: string, completed: boolean) => Promise<unknown>;
}

const fullName = (firstName: string, lastName: string) =>
  [firstName, lastName].filter(Boolean).join(' ');

const withoutCompanyName = (applicationName: string, companyName?: string) => {
  const normalizedCompanyName = companyName?.trim();
  if (!normalizedCompanyName || !applicationName.includes('|')) {
    return applicationName;
  }

  return applicationName
    .split('|')
    .map((part) => part.trim())
    .filter(
      (part) =>
        part.localeCompare(normalizedCompanyName, undefined, {
          sensitivity: 'base',
        }) !== 0,
    )
    .join(' | ');
};

export const ProgramTasks = ({
  tasks,
  status,
  mutationStatusByTaskId,
  applicationCompanyName,
  counts: taskCounts,
  filter: controlledFilter,
  focusedTaskId,
  page = 1,
  pageSize = 0,
  showApplicationColumn = false,
  total = 0,
  onFilterChange,
  onOpenApplication,
  onPageChange,
  onRetry,
  onSetCompleted,
}: ProgramTasksProps) => {
  const { t } = useTranslations();
  const { selectedLocale } = useLocale();
  const titleId = useId();
  const [localFilter, setLocalFilter] =
    useState<ClientTaskStatusFilter>('all');
  const filter = controlledFilter ?? localFilter;
  const [selectedTaskId, setSelectedTaskId] = useState<string | undefined>(
    focusedTaskId,
  );
  const isLoading = status === 'idle' || status === 'pending';
  const isError = status === 'failed';
  const selectedTask = tasks.find((task) => task.task_id === selectedTaskId);

  useEffect(() => {
    if (focusedTaskId) setSelectedTaskId(focusedTaskId);
  }, [focusedTaskId]);
  const handleSetCompleted = useCallback(
    async (taskId: string, completed: boolean) => {
      await onSetCompleted(taskId, completed);
      antdMessage.success(
        t(
          completed
            ? 'program-workspace|taskCompletedSuccess'
            : 'program-workspace|taskReopenedSuccess',
        ),
      );
    },
    [onSetCompleted, t],
  );

  const loadedCounts = useMemo(
    () =>
      tasks.reduce(
        (result, task) => {
          result.all += 1;
          result[task.status] += 1;
          return result;
        },
        { all: 0, open: 0, completed: 0 },
      ),
    [tasks],
  );
  const counts = taskCounts ?? loadedCounts;
  const visibleTasks = useMemo(
    () =>
      filter === 'all' ? tasks : tasks.filter((task) => task.status === filter),
    [filter, tasks],
  );
  const columns = useMemo(
    () =>
      buildTaskTableColumns({
        status: filter,
        showAssociatedObjects: showApplicationColumn,
        labels: {
          task: t('program-workspace|taskColumnTask'),
          assignee: t('program-workspace|taskColumnAssignee'),
          assignedBy: t('program-workspace|taskColumnAssignedBy'),
          associatedObjects: t('program-workspace|taskColumnApplication'),
          due: t('program-workspace|taskColumnDue'),
          completed: t('program-workspace|taskColumnCompleted'),
        },
      }),
    [filter, showApplicationColumn, t],
  );
  const rows = useMemo<SortedTableRow[]>(
    () =>
      visibleTasks.map((task) => {
        const assigneeNames = task.client_assignees
          .map((assignee) => fullName(assignee.first_name, assignee.last_name))
          .filter(Boolean);
        const creatorName = fullName(
          task.created_by.first_name,
          task.created_by.last_name,
        );
        const applicationAssociations = task.associations.filter(
          ({ association_type, program_id }) =>
            association_type === 'program' && !!program_id,
        );
        const applicationNames = applicationAssociations
          .map(({ name }) => withoutCompanyName(name, applicationCompanyName))
          .filter(Boolean);
        const daysRemaining = getTaskDaysRemaining(task.due_at);
        const duePill: { label: string; tone: TaskTableStatusPillTone } =
          daysRemaining === null
            ? {
                label: t('program-workspace|taskNoDueDate'),
                tone: 'neutral',
              }
            : daysRemaining < 0
              ? {
                  label: t('program-workspace|taskOverdue'),
                  tone: 'danger',
                }
              : {
                  label: t('program-workspace|taskDaysRemaining', {
                    count: daysRemaining,
                  }),
                  tone: daysRemaining <= 7 ? 'warning' : 'success',
                };
        const mutationPending =
          mutationStatusByTaskId[task.task_id] === 'pending';

        const assigneeCell = (
          <TaskTableOwnerPills
            names={assigneeNames}
            fallbackLabel={t('program-workspace|taskAssignedToClientShort')}
          />
        );
        const assignedByCell = (
          <TaskTableOwnerPills
            names={creatorName ? [creatorName] : []}
            fallbackLabel="-"
          />
        );
        const applicationCell = (
          <TaskTableOwnerPills
            names={applicationNames}
            fallbackLabel="-"
            onNameClick={
              onOpenApplication
                ? (_name, index) => {
                    const programId =
                      applicationAssociations[index]?.program_id;
                    if (programId) onOpenApplication(programId);
                  }
                : undefined
            }
          />
        );
        const dueDateCell = (
          <TaskTableCenteredCell key={`${task.task_id}:due`}>
            {task.status === 'completed' ? (
              <TaskTableMutedText>-</TaskTableMutedText>
            ) : (
              <TaskTableStatusPill $tone={duePill.tone}>
                {duePill.label}
              </TaskTableStatusPill>
            )}
          </TaskTableCenteredCell>
        );
        const completedDateCell = (
          <TaskTableCenteredCell key={`${task.task_id}:completed`}>
            {task.status === 'completed' && task.completed_at ? (
              <TaskTableCellText>
                {formatUtcDate(task.completed_at, selectedLocale)}
              </TaskTableCellText>
            ) : (
              <TaskTableMutedText>-</TaskTableMutedText>
            )}
          </TaskTableCenteredCell>
        );
        const cells = [
          <Checkbox
            key={`${task.task_id}:completion`}
            className="sorted-table-clickable"
            checked={task.status === 'completed'}
            disabled={mutationPending}
            aria-label={t('program-workspace|taskCompletionToggle', {
              title: task.title,
            })}
            onChange={(event) => {
              void handleSetCompleted(task.task_id, event.target.checked).catch(
                () => undefined,
              );
            }}
          />,
          <TaskTableTitleButton
            type="button"
            key={`${task.task_id}:title`}
            onClick={() => setSelectedTaskId(task.task_id)}
          >
            <TaskTableTitleText>{task.title}</TaskTableTitleText>
          </TaskTableTitleButton>,
          assigneeCell,
          assignedByCell,
        ];
        const sortValues: Array<string | number | null> = [
          null,
          task.title,
          assigneeNames.join(' '),
          creatorName,
        ];

        if (showApplicationColumn) {
          cells.push(applicationCell);
          sortValues.push(applicationNames.join(' '));
        }

        if (filter !== 'completed') {
          cells.push(dueDateCell);
          sortValues.push(task.due_at);
        }

        if (filter !== 'open') {
          cells.push(completedDateCell);
          sortValues.push(task.completed_at);
        }

        return {
          key: task.task_id,
          cells,
          sortValues,
          onClick: () => setSelectedTaskId(task.task_id),
        };
      }),
    [
      filter,
      mutationStatusByTaskId,
      handleSetCompleted,
      selectedLocale,
      showApplicationColumn,
      applicationCompanyName,
      onOpenApplication,
      t,
      visibleTasks,
    ],
  );
  const hasVisibleMutationError = visibleTasks.some(
    (task) => mutationStatusByTaskId[task.task_id] === 'failed',
  );
  return (
    <TaskSection aria-busy={isLoading} aria-labelledby={titleId}>
      <Typography elementTheme="h3" id={titleId}>
        {t('program-workspace|tasksTitle')}
      </Typography>
      {isError ? (
        <>
          <TaskError role="alert">
            <Typography>{t('program-workspace|tasksLoadError')}</Typography>
          </TaskError>
          <Button variant="outline" onClick={onRetry}>
            {t('program-workspace|tasksRetry')}
          </Button>
        </>
      ) : isLoading && tasks.length === 0 ? (
        <Loading />
      ) : (
        <TaskTable
          filters={
            counts.all > 0 ? (
              <TaskTableFilterBar>
                <TaskTableSegmentedFilter
                  ariaLabel={t('program-workspace|tasksFilterLabel')}
                  value={filter}
                  options={(['all', 'open', 'completed'] as const).map(
                    (value) => ({
                      key: value,
                      label: t(`program-workspace|tasksFilter${value}`),
                      count: counts[value],
                    }),
                  )}
                  onChange={(nextFilter: ClientTaskStatusFilter) => {
                    if (controlledFilter === undefined) {
                      setLocalFilter(nextFilter);
                    }
                    onFilterChange?.(nextFilter);
                  }}
                />
              </TaskTableFilterBar>
            ) : undefined
          }
          columns={columns}
          rows={rows}
          naturalHeight
          loading={false}
          emptyLabel={t('program-workspace|tasksEmpty')}
        />
      )}
      {onPageChange && pageSize > 0 && total > pageSize ? (
        <TaskPagination>
          <Pagination
            current={page}
            pageSize={pageSize}
            showSizeChanger={false}
            total={total}
            onChange={onPageChange}
          />
        </TaskPagination>
      ) : null}
      {hasVisibleMutationError ? (
        <TaskError role="alert">
          <Typography>{t('program-workspace|taskUpdateError')}</Typography>
        </TaskError>
      ) : null}
      <TaskDetailDrawer
        task={selectedTask}
        selectedLocale={selectedLocale}
        mutationPending={
          selectedTask
            ? mutationStatusByTaskId[selectedTask.task_id] === 'pending'
            : false
        }
        onClose={() => setSelectedTaskId(undefined)}
        onSetCompleted={handleSetCompleted}
      />
    </TaskSection>
  );
};
